import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Mail, MessageSquare, Loader2, Save, Check, AlertCircle, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Toggle = ({ checked, onChange, testId, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-blue-600' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const CommunicationPreferencesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState(true);
  const [message, setMessage] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [smsOptedIn, setSmsOptedIn] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/communication-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setAvailable(false);
        return;
      }
      if (!res.ok) throw new Error('Failed to load preferences');
      const data = await res.json();
      setEmailEnabled(data?.channels?.email ?? true);
      setSmsEnabled(data?.channels?.sms ?? true);
      setSmsOptedIn(data?.sms_consent?.opted_in ?? false);
      setAvailable(true);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Unable to load preferences.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/communication-preferences`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channels: { email: emailEnabled, sms: smsEnabled },
          sms_consent: { opted_in: smsOptedIn },
        }),
      });
      if (!res.ok) throw new Error('Failed to save preferences');
      const data = await res.json();
      setEmailEnabled(data?.channels?.email ?? emailEnabled);
      setSmsEnabled(data?.channels?.sms ?? smsEnabled);
      setSmsOptedIn(data?.sms_consent?.opted_in ?? smsOptedIn);
      setMessage({ type: 'success', text: 'Communication preferences saved.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Unable to save preferences.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="communication-preferences-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-7 h-7 text-blue-600" />
            Communication Preferences
          </h1>
          <p className="text-gray-600 mt-1">
            Choose how we contact you. Important safety, compliance, and account notifications are always delivered.
          </p>
        </div>

        {message && (
          <div
            className={`p-4 rounded-lg flex items-start gap-3 ${
              message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}
            data-testid="comm-prefs-message"
          >
            {message.type === 'success' ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>{message.text}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[240px]" data-testid="comm-prefs-loading">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="comm-prefs-unavailable">
            <p className="text-gray-700">Communication preferences are not currently available.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
              <div className="flex items-center justify-between p-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Email notifications</p>
                    <p className="text-sm text-gray-500">Optional updates sent to your email address.</p>
                  </div>
                </div>
                <Toggle checked={emailEnabled} onChange={setEmailEnabled} testId="comm-prefs-email-toggle" />
              </div>

              <div className="flex items-center justify-between p-6">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">SMS notifications</p>
                    <p className="text-sm text-gray-500">Optional text messages to your phone.</p>
                  </div>
                </div>
                <Toggle checked={smsEnabled} onChange={setSmsEnabled} testId="comm-prefs-sms-toggle" />
              </div>

              <div className="flex items-center justify-between p-6">
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">SMS consent</p>
                    <p className="text-sm text-gray-500">
                      I agree to receive optional SMS messages. Message and data rates may apply.
                    </p>
                  </div>
                </div>
                <Toggle checked={smsOptedIn} onChange={setSmsOptedIn} testId="comm-prefs-sms-consent-toggle" />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="comm-prefs-save-button"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Save Preferences'}
              </button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CommunicationPreferencesPage;
